import type { Size, XyzDataPoint } from '../types';

const LABEL_FONT = '12px system-ui, -apple-system, "Segoe UI", sans-serif';
const LABEL_TITLE_FONT =
  '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';

/** How many component names are listed before the rest are summarised. */
const NAMES_SHOWN = 4;

const PADDING = 8;
const LINE_HEIGHT = 16;

/** The tooltip heading for the components at one mark: the name of one, or how many. */
export const toComponentHeading = (points: XyzDataPoint[]): string =>
  points.length === 1 ? points[0].label : `${points.length} components here`;

/** The first few names under a heading that counted them, and how many more there are. */
export const toComponentNameLines = (points: XyzDataPoint[]): string[] => {
  if (points.length < 2) return [];
  const names = points.slice(0, NAMES_SHOWN).map(({ label }) => label);
  const remaining = points.length - names.length;
  return [...names, ...(remaining > 0 ? [`and ${remaining} more`] : [])];
};

export type TooltipArgs = {
  context: CanvasRenderingContext2D;
  anchor: { screenX: number; screenY: number };
  heading: string;
  lines: string[];
  viewport: Size;
  inkColor: string;
  surfaceColor: string;
  gridColor: string;
};

/** A heading and lines of text in a rounded box beside `anchor`, kept inside the viewport. */
export const drawTooltip = ({
  context,
  anchor,
  heading,
  lines,
  viewport,
  inkColor,
  surfaceColor,
  gridColor,
}: TooltipArgs): void => {
  context.save();
  context.font = LABEL_TITLE_FONT;
  const headingWidth = context.measureText(heading).width;
  context.font = LABEL_FONT;
  const width =
    Math.max(
      headingWidth,
      ...lines.map(line => context.measureText(line).width)
    ) +
    PADDING * 2;
  const height = (lines.length + 1) * LINE_HEIGHT + PADDING * 2;

  const { screenX, screenY } = anchor;
  /* Kept inside the viewport: a tooltip for a marker near the right edge would otherwise be clipped
     exactly when the marker is hardest to identify. */
  const left = Math.min(
    Math.max(4, screenX + 12),
    Math.max(4, viewport.width - width - 4)
  );
  const top = Math.min(
    Math.max(4, screenY - height - 12),
    Math.max(4, viewport.height - height - 4)
  );

  context.globalAlpha = 0.97;
  context.fillStyle = surfaceColor;
  context.strokeStyle = gridColor;
  context.lineWidth = 1;
  context.beginPath();
  context.roundRect(left, top, width, height, 6);
  context.fill();
  context.stroke();

  context.globalAlpha = 1;
  context.fillStyle = inkColor;
  context.textBaseline = 'top';
  context.textAlign = 'left';
  context.font = LABEL_TITLE_FONT;
  context.fillText(heading, left + PADDING, top + PADDING);

  context.font = LABEL_FONT;
  lines.forEach((line, index) => {
    context.fillText(
      line,
      left + PADDING,
      top + PADDING + (index + 1) * LINE_HEIGHT
    );
  });

  context.restore();
};
